/**
 * Float distribution histogram for Check #19.
 * Pure SVG output — no external charting library, no image files.
 *
 * Section 8 of Prompt 7B.
 * @module pdf/helpers/float-histogram-builder
 */

import { HISTOGRAM_PALETTE } from './float-histogram-palette.js';
// SURFACE-COHERE (R14): the bin table + interpretation live in the engine
// (src/checks/floatBuckets.js); this helper imports them so nothing here
// defines bins or composes the sentence. The exports below are re-exports for
// existing callers (the dashboard + tests import them from this module).
import {
  buildFloatBinTable,
  interpretDistribution as engineInterpretDistribution,
  colorForBinId,
  NO_DATA_LABEL as ENGINE_NO_DATA_LABEL,
  NO_DATA_SUBLABEL as ENGINE_NO_DATA_SUBLABEL,
} from '../../checks/floatBuckets.js';

// V-15 (v3.23): the four semantic bucket colours come from the ONE shared
// palette that the dashboard FloatHistogram also imports (single source). The
// SVG chrome colours (axis / label / secondary label) are builder-local — not
// part of the duplicated bucket palette. Byte-identical to the prior literals
// (grey and labelSec were both '#6C757D').
const COLOR = {
  ...HISTOGRAM_PALETTE,
  axis:     '#DEE2E6',
  label:    '#1A1A2E',
  labelSec: '#6C757D',
};

/**
 * DASH-POLISH-C: the canonical no-data bucket strings — the SINGLE source for
 * both the PDF chart bar and the dashboard chart bar (Rule 12). The dashboard
 * imports these constants; no re-typed literal of a user-facing label. Values
 * are unchanged from the pre-existing PDF literals ('No data' / 'no float data').
 */
export const NO_DATA_LABEL = ENGINE_NO_DATA_LABEL;
export const NO_DATA_SUBLABEL = ENGINE_NO_DATA_SUBLABEL;

/**
 * Bucket definition. Buckets are exclusive: each activity falls into
 * exactly one bucket based on its total float in working days.
 */
// F-4 (V-06, amendment v3.19 §10): ruled palette semantics — the healthy band
// (11–44 d) is the ONLY green; negative is critical red; zero, near-critical
// (1–10) and every excessive band (45–100 / 101–200 / 200+) are warning amber;
// no-data is neutral grey. PDF + dashboard share this builder so both move
// together (Rule 12). Was: zero=red, 11–44=amber, 45–100=green, 101+/200+=grey.
// The default (N=10, E=44) bin table with F-4 colours attached — byte-identical
// to the pre-SURFACE-COHERE literal. Used by bucketFloats (test-only raw-days
// path); the report renders the engine's emitted buckets instead.
export const BUCKETS = buildFloatBinTable(10, 44).map((b) => ({
  ...b,
  color: colorForBinId(HISTOGRAM_PALETTE, b.id),
}));

/**
 * Bucket a list of float-day numbers. Non-numeric values are skipped.
 *
 * @param {Array<number|null>} floatDays
 * @returns {Array<{id, label, range, count, color}>}
 */
export function bucketFloats(floatDays) {
  const counts = BUCKETS.map((b) => ({ ...b, count: 0 }));
  for (const raw of floatDays || []) {
    if (raw === null || raw === undefined) continue;
    const d = Number(raw);
    if (!Number.isFinite(d)) continue;
    for (const b of counts) {
      if (b.test(d)) {
        b.count++;
        break;
      }
    }
  }
  return counts.map(({ test, ...rest }) => rest);
}

/**
 * Generate a one-sentence interpretation from bucket counts.
 *
 * 10D-1.5/Sub-2 fix-up X13/X14: when a `denominator` is provided, the
 * percentage base is the canonical engine denominator (e.g. 63 — analyzable
 * activities) rather than the sum of bucket counts (e.g. 62 — analyzable
 * minus null-float entries). The buckets only contain numeric-float entries,
 * so the bucket sum understates the population by the null-float count.
 */
// SURFACE-COHERE (R14): re-export the engine's interpretation. The engine
// signature is (buckets, denominator, excessiveFloatDays=44); two-argument
// callers here and in the dashboard get the default E=44 (byte-identical to the
// prior local implementation).
export const interpretDistribution = engineInterpretDistribution;

/**
 * SURFACE-COHERE (R14): build a histogram view from the engine's EMITTED
 * #19 buckets (the canonical 7-bin + noData set) and its `distribution_summary`
 * — no re-binning, no re-composition. Colours the bars by bin id from the F-4
 * palette; drops the noData bar when its count is 0 (both surfaces follow this
 * PDF rule). This is the report render path.
 *
 * @param {Array<{id,label,range,count,activityIds?}>} engineBuckets
 * @param {string} distributionSummary  checkResult.distribution_summary
 * @returns {{buckets, svgString, interpretation}}
 */
export function buildHistogramFromEngineBuckets(engineBuckets, distributionSummary) {
  // Legacy tolerance: a stored report's dashboard.json predating this era
  // carries the old 6 buckets with no `id` and the noData marker range:'null'.
  // Resolve an id from the old label so colours/labels still render (display
  // only — no recomputation); the sentence is simply absent for such reports.
  const idOf = (b) => {
    if (b.id) return b.id;
    const s = String(b.label || '').toLowerCase();
    if (b.range === 'null' || s.includes('no float') || s.includes('no data')) return 'noData';
    if (s.includes('neg')) return 'negative';
    if (s.includes('zero')) return 'zero';
    if (s.includes('near')) return 'nearCrit';
    if (s.includes('low')) return 'normal';
    if (s.includes('excess')) return 'excessive';
    return 'normal';
  };
  const drawable = (engineBuckets || [])
    .map((b) => ({ b, id: idOf(b) }))
    .filter(({ b, id }) => id !== 'noData' || (b.count || 0) > 0)
    .map(({ b, id }) => ({
      id,
      label: id === 'noData' ? NO_DATA_LABEL : b.label,
      range: id === 'noData' ? NO_DATA_SUBLABEL : b.range,
      count: b.count || 0,
      color: colorForBinId(HISTOGRAM_PALETTE, id),
    }));
  // total = the analysable population (all engine buckets incl. noData) — the
  // "(N activities)" title suffix. Matches the pre-era `denominator` base.
  const total = (engineBuckets || []).reduce((s, b) => s + (b.count || 0), 0);
  return {
    buckets: drawable,
    svgString: renderHistogramSvg(drawable),
    interpretation: distributionSummary || '',
    total,
  };
}

// ==================== SVG RENDERING ====================

const ESC_MAP = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' };
function esc(s) {
  return String(s).replace(/[&<>"']/g, (c) => ESC_MAP[c]);
}

/**
 * Render an inline SVG string for the histogram.
 *
 * @param {Array} buckets - output of bucketFloats()
 * @param {object} [opts]
 * @param {number} [opts.width=560]
 * @param {number} [opts.height=220]
 * @returns {string} SVG markup
 */
export function renderHistogramSvg(buckets, opts = {}) {
  const width = opts.width || 560;
  const height = opts.height || 220;
  const marginLeft = 40;
  const marginRight = 10;
  const marginTop = 18;
  const marginBottom = 50;

  const plotW = width - marginLeft - marginRight;
  const plotH = height - marginTop - marginBottom;

  const maxCount = Math.max(1, ...buckets.map((b) => b.count));
  const gap = 8;
  const barW = (plotW - gap * (buckets.length - 1)) / buckets.length;

  const yScale = (v) => plotH * (1 - v / maxCount);

  const bars = buckets.map((b, i) => {
    const x = marginLeft + i * (barW + gap);
    const h = plotH - yScale(b.count);
    const y = marginTop + yScale(b.count);
    // DASH-POLISH-B P1b: emit the sub-label <text> ONLY when the bucket carries
    // a range — a bucket with an empty range (the dashboard's no-data bar) gets
    // no sub-label element, avoiding an empty-<text> artefact. Every PDF bucket
    // has a non-empty range (numeric ranges + 'no float data'), so the PDF SVG
    // is byte-identical (proven by the date-matched text diff at closure).
    const subLabel = b.range
      ? `\n      <text x="${(x + barW / 2).toFixed(2)}" y="${(marginTop + plotH + 26).toFixed(2)}" text-anchor="middle" font-family="'Poppins', sans-serif" font-weight="400" font-size="8" fill="${COLOR.labelSec}">${esc(b.range)}</text>`
      : '';
    return `
    <g>
      <rect x="${x.toFixed(2)}" y="${y.toFixed(2)}" width="${barW.toFixed(2)}" height="${h.toFixed(2)}" fill="${b.color}" rx="2"/>
      <text x="${(x + barW / 2).toFixed(2)}" y="${(y - 4).toFixed(2)}" text-anchor="middle" font-family="'Poppins', sans-serif" font-weight="700" font-size="10" fill="${COLOR.label}">${b.count}</text>
      <text x="${(x + barW / 2).toFixed(2)}" y="${(marginTop + plotH + 14).toFixed(2)}" text-anchor="middle" font-family="'Poppins', sans-serif" font-weight="500" font-size="9" fill="${COLOR.label}">${esc(b.label)}</text>${subLabel}
    </g>`;
  }).join('');

  const yAxisLine = `<line x1="${marginLeft}" y1="${marginTop}" x2="${marginLeft}" y2="${marginTop + plotH}" stroke="${COLOR.axis}" stroke-width="1"/>`;
  const xAxisLine = `<line x1="${marginLeft}" y1="${marginTop + plotH}" x2="${marginLeft + plotW}" y2="${marginTop + plotH}" stroke="${COLOR.axis}" stroke-width="1"/>`;

  // Y axis ticks (0, max)
  const yTicks = `
  <text x="${marginLeft - 6}" y="${(marginTop + plotH + 3).toFixed(2)}" text-anchor="end" font-family="'Poppins', sans-serif" font-weight="400" font-size="8" fill="${COLOR.labelSec}">0</text>
  <text x="${marginLeft - 6}" y="${(marginTop + 3).toFixed(2)}" text-anchor="end" font-family="'Poppins', sans-serif" font-weight="400" font-size="8" fill="${COLOR.labelSec}">${maxCount}</text>`;

  return `<svg viewBox="0 0 ${width} ${height}" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:auto;">${yAxisLine}${xAxisLine}${yTicks}${bars}</svg>`;
}

/**
 * Convenience wrapper: buckets + svg + interpretation in one object.
 *
 * @param {Array<number|null>} floatDays
 * @param {object} [opts]
 * @param {number} [opts.denominator] — canonical engine denominator
 *   (analyzable activity count). When provided, the interpretation
 *   percentage and the returned `total` use this base instead of the
 *   bucket sum. Fix-up X13/X14: keeps display population aligned with
 *   the engine canonical denominator (e.g. 63 vs the bucket sum 62).
 * @param {number} [opts.noDataCount] — count of analyzable activities whose
 *   total-float is null/missing (engine emission: denominator minus
 *   `analyzable_float_days.length`). When > 0, an extra "No data" bucket is
 *   appended so the chart bars themselves sum to the canonical denominator,
 *   eliminating the chart-vs-title mismatch fixed in PDF-POLISH-1 D16.
 * @returns {{buckets, svgString, interpretation, total}}
 */
export function buildFloatHistogram(floatDays, opts = {}) {
  const buckets = bucketFloats(floatDays);

  // PDF-POLISH-1 D16: surface null-float entries as a dedicated bucket so the
  // visible bar counts add up to the same population the title/footer report.
  // Without this, `bucketFloats` (which skips non-numeric entries) understates
  // the population by the engine's null-float count, and viewers see chart
  // bars that sum to 62 next to a title of 63 — the exact defect that fix-up
  // X13/X14 only papered over by patching the title.
  const noDataCount = (typeof opts.noDataCount === 'number' && opts.noDataCount > 0)
    ? opts.noDataCount
    : 0;
  if (noDataCount > 0) {
    buckets.push({
      id: 'noData',
      // MEGAC-FIX-B R2: planner-facing sub-label — never the raw engine token
      // 'null float'. DASH-POLISH-C: both strings now come from the exported
      // NO_DATA_* constants (single source; the dashboard imports the same).
      label: NO_DATA_LABEL,
      range: NO_DATA_SUBLABEL,
      count: noDataCount,
      color: COLOR.grey,
    });
  }

  const svgString = renderHistogramSvg(buckets);
  const bucketSum = buckets.reduce((s, b) => s + b.count, 0);
  const total = (typeof opts.denominator === 'number' && opts.denominator > 0)
    ? opts.denominator
    : bucketSum;
  const interpretation = interpretDistribution(buckets, total);
  return { buckets, svgString, interpretation, total };
}
